import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { usePartsStore } from '../../store/partsStore';
import LoadingSpinner from '../LoadingSpinner';
import ErrorDisplay from '../common/ErrorDisplay';
import { Heart, Shield, User, Plus, AlertTriangle, Trash2, Bot, Sparkles, Flame, Star } from 'lucide-react';
import { Part } from '../../types';
import AddPartModal from './AddPartModal';
import PartCard from './PartCard';
import HigherPowerForm from './HigherPowerForm';
import HigherPowerCard from './HigherPowerCard';
import { supabase } from '../../lib/supabase';

interface Profile {
  higher_power_enabled?: boolean;
  higher_power_name?: string;
  higher_power_message?: string;
  higher_power_emoji?: string;
  higher_power_type?: string[];
  higher_power_connection?: string[];
  higher_power_experiences?: string[];
}

export default function PartsFamily() {
  const { user } = useAuth();
  const { parts, selectedPartId, isLoading, error: storeError, actions } = usePartsStore();
  const [selectedPart, setSelectedPart] = useState<Part | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHigherPowerForm, setShowHigherPowerForm] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    if (user) {
      actions.fetchParts();
      fetchProfile();
    }
  }, [user, actions]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('higher_power_enabled, higher_power_name, higher_power_message, higher_power_emoji, higher_power_type, higher_power_connection, higher_power_experiences')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const handleAddPart = async (part: Omit<Part, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    try {
      if (!user) {
        throw new Error('You must be logged in to add parts');
      }

      if (selectedPart) {
        await actions.updatePart(selectedPart.id, part);
      } else {
        await actions.addPart(part);
      }
      setShowAddModal(false);
      setSelectedPart(null);
    } catch (err) {
      console.error('Error saving part:', err);
      throw err;
    }
  };

  const handleDeletePart = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user) {
      setDeleteError('You must be logged in to delete parts');
      return;
    }

    if (!confirm('Are you sure you want to delete this part? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleteError(null);
      await actions.deletePart(id);
    } catch (err) {
      console.error('Error deleting part:', err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete part');
    }
  };

  const handlePartClick = (partId: string) => {
    const clickedPart = parts.find(p => p.id === partId);
    if (clickedPart) {
      setSelectedPart(clickedPart);
      setShowAddModal(true);
    }
  };

  const selfPart = parts.find(p => p.type === 'self');
  const activeParts = parts.filter(p => p.type !== 'self' && p.status !== 'integrated');
  const integratedParts = parts.filter(p => p.status === 'integrated');

  const stats = {
    protector: parts.filter(p => p.type === 'protector').length,
    exile: parts.filter(p => p.type === 'exile').length,
    manager: parts.filter(p => p.type === 'manager').length,
    rescuer: parts.filter(p => p.type === 'rescuer').length
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto p-6 text-center">
        <ErrorDisplay
          error={{
            code: 'auth_required',
            message: 'Please sign in to view and manage your parts',
            details: 'Authentication is required to access this feature'
          }}
        />
      </div>
    );
  }

  if (isLoading) return <LoadingSpinner />;

  if (storeError) {
    return (
      <ErrorDisplay
        error={{
          code: 'parts_error',
          message: storeError,
          details: 'Please try again or contact support if the problem persists.'
        }}
        onRetry={actions.fetchParts}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Parts List */}
        <div className="order-1 flex-1">
          <div className="bg-white rounded-xl shadow-lg border border-mint-200 p-6">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Bot className="h-6 w-6 text-amber-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-midnight-800">Internal Family</h2>
                    <div className="flex items-center space-x-2 text-sm text-amber-600">
                      <Shield className="h-4 w-4" />
                      <span>Safe Space Active</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Buttons stacked on mobile, side by side on larger screens */}
              <div className="flex flex-col sm:flex-row gap-3">
                {!profile?.higher_power_enabled && (
                  <button
                    onClick={() => setShowHigherPowerForm(true)}
                    className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-bubblegum-100 text-bubblegum-700 rounded-lg hover:bg-bubblegum-200 transition-colors"
                  >
                    <Star className="h-5 w-5" />
                    <span>Add Higher Power</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedPart(null);
                    setShowAddModal(true);
                  }}
                  className="w-full sm:w-auto flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-500 to-marigold-500 text-white px-4 py-2 rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-sm"
                >
                  <Plus className="h-5 w-5" />
                  <span>Add Part</span>
                </button>
              </div>
            </div>

            {parts.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                <p className="text-midnight-800">No parts added yet.</p>
                <p className="text-sm text-midnight-500 mt-1">
                  Start mapping your internal system by adding your first part.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                {/* Higher Power Card */}
                {profile?.higher_power_enabled && profile.higher_power_name && (
                  <HigherPowerCard
                    name={profile.higher_power_name}
                    message={profile.higher_power_message}
                    emoji={profile.higher_power_emoji || '⭐'}
                    onConnect={() => setShowHigherPowerForm(true)}
                  />
                )}

                {/* Self Part */}
                {selfPart && (
                  <PartCard
                    key={selfPart.id}
                    part={selfPart}
                    isSelected={selectedPartId === selfPart.id}
                    onClick={handlePartClick}
                    integratedParts={integratedParts}
                  />
                )}
                
                {/* Active Parts Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {activeParts.map(part => (
                    <PartCard
                      key={part.id}
                      part={part}
                      isSelected={selectedPartId === part.id}
                      onClick={handlePartClick}
                      onDelete={(e) => handleDeletePart(part.id, e)}
                    />
                  ))}
                </div>

                {/* Integrated Parts Section */}
                {integratedParts.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-mint-200">
                    <div className="flex items-center space-x-3 mb-6">
                      <Sparkles className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-semibold text-midnight-800">Integrated Parts</h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {integratedParts.map(part => (
                        <div
                          key={part.id}
                          className="group relative p-4 bg-mint-50 hover:bg-mint-100 rounded-lg border border-mint-200 
                            transition-all hover:-translate-y-1 hover:shadow-lg cursor-pointer"
                          onClick={() => handlePartClick(part.id)}
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl group-hover:scale-110 transition-transform">
                              {part.emoji}
                            </span>
                            <div className="flex-1 min-w-0 text-left">
                              <p className="font-medium text-midnight-800 truncate">
                                {part.name}
                              </p>
                              <p className="text-xs text-midnight-500 truncate capitalize">
                                {part.type}
                              </p>
                            </div>
                          </div>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePart(part.id, e);
                            }}
                            className="absolute -top-2 -right-2 p-1.5 rounded-full bg-white 
                              opacity-0 group-hover:opacity-100 hover:bg-red-50 
                              text-midnight-400 hover:text-red-500 transition-all cursor-pointer
                              border border-mint-200 shadow-sm"
                          >
                            <Trash2 className="h-3 w-3" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats Sidebar */}
        <div className="order-2 lg:w-72 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-mint-200 p-6">
            <h2 className="text-sm font-medium text-midnight-700 mb-6">System Overview</h2>
            <div className="space-y-4">
              {[
                {
                  icon: <Shield className="h-4 w-4" />,
                  label: "Protectors",
                  value: stats.protector,
                  maxValue: 10
                },
                {
                  icon: <Heart className="h-4 w-4" />,
                  label: "Exiles",
                  value: stats.exile,
                  maxValue: 10
                },
                {
                  icon: <User className="h-4 w-4" />,
                  label: "Managers",
                  value: stats.manager,
                  maxValue: 10
                },
                {
                  icon: <Flame className="h-4 w-4" />,
                  label: "Rescuers",
                  value: stats.rescuer,
                  maxValue: 10
                }
              ].map((stat, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-mint-100 text-mint-600 rounded-lg">
                      {stat.icon}
                    </div>
                    <span className="text-sm text-midnight-600">{stat.label}</span>
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="font-medium text-midnight-800">
                      {stat.value}
                    </span>
                    <span className="text-xs text-midnight-500">
                      /{stat.maxValue}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Part Modal */}
      {showAddModal && (
        <AddPartModal
          onClose={() => {
            setShowAddModal(false);
            setSelectedPart(null);
          }}
          onAdd={handleAddPart}
          part={selectedPart}
        />
      )}

      {/* Higher Power Form */}
      {showHigherPowerForm && (
        <HigherPowerForm
          onComplete={() => {
            setShowHigherPowerForm(false);
            fetchProfile();
          }}
          onClose={() => setShowHigherPowerForm(false)}
          onDelete={profile?.higher_power_enabled ? () => {
            setShowHigherPowerForm(false);
            fetchProfile();
          } : undefined}
          existingData={profile?.higher_power_enabled ? {
            name: profile.higher_power_name || '',
            type: profile.higher_power_type || [],
            connection: profile.higher_power_connection || [],
            experiences: profile.higher_power_experiences || [],
            message: profile.higher_power_message || '',
            emoji: profile.higher_power_emoji || '⭐'
          } : undefined}
        />
      )}
    </div>
  );
}